import React from 'react';
import { Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import ManagerOverview from './ManagerOverview';
import ProjectManagement from './ProjectManagement';
import TaskManagement from './TaskManagement';
import UserManagement from './UserManagement';
import SpecialtyManagement from './SpecialtyManagement';
import TeamManagement from './TeamManagement';
import ClientManagement from './ClientManagement';
import PricelistManagement from './PricelistManagement';
import TimeEntryManagement from './TimeEntryManagement';
import TimeApproval from './TimeApproval';
import ExpenseApprovalTable from './ExpenseApprovalTable';
import TimeOffManagement from './TimeOffManagement';
import ManagerTimeOffCalendar from '../../pages/ManagerTimeOffCalendar';
import TimeEntryReports from './TimeEntryReports';
import ExpenseReports from '../../pages/manager/ExpenseReports';
import OrganizationSettings from './OrganizationSettings';
import RecentActivityPage from './RecentActivityPage';
import {
  GridIcon,
  FolderIcon,
  ClipboardIcon,
  UserIcon,
  UsersIcon,
  Building2Icon,
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  DollarSignIcon,
  FileTextIcon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
} from '../icons';

// TradUp Style Theme Toggle Pill
const ThemeTogglePill: React.FC = () => {
  const { setTheme, isDark } = useTheme();

  return (
    <div
      className={`flex items-center p-1 rounded-xl ${
        isDark ? 'bg-slate-900/80' : 'bg-slate-100'
      }`}
    >
      <button
        onClick={() => setTheme('light')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
          !isDark
            ? 'bg-white text-slate-900 shadow-sm'
            : 'text-slate-400 hover:text-slate-300'
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        Light
      </button>
      <button
        onClick={() => setTheme('dark')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center ${
          isDark
            ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-500/25'
            : 'text-slate-500 hover:text-slate-600'
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
        Dark
      </button>
    </div>
  );
};

type NavItem = {
  name: string;
  href?: string;
  icon: React.ReactNode;
  current: boolean;
  badge?: number;
  children?: { name: string; href: string }[];
};

type NavSection = {
  title: string;
  items: NavItem[];
};

// Premium Lucide-style icons
const Icons = {
  grid: <GridIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  folder: <FolderIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  clipboard: <ClipboardIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  user: <UserIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  users: <UsersIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  building: <Building2Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  clock: <ClockIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  checkCircle: (
    <CheckCircleIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />
  ),
  calendar: <CalendarIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  dollarSign: (
    <DollarSignIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />
  ),
  fileText: <FileTextIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  settings: <SettingsIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />,
  chevronLeft: <ChevronLeftIcon className="w-5 h-5" strokeWidth={1.5} />,
  chevronDown: <ChevronDownIcon className="w-4 h-4" strokeWidth={1.5} />,
};

const ManagerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState<
    Record<string, boolean>
  >({});
  const isAdmin = user?.role === 'admin';

  const isActive = (href: string) => {
    if (href === '/manager') return location.pathname === '/manager';
    return location.pathname.startsWith(href);
  };

  const sections: NavSection[] = [
    {
      title: 'Overview',
      items: [
        {
          name: t('manager.managementDashboard'),
          href: '/manager',
          icon: Icons.grid,
          current: isActive('/manager') && location.pathname === '/manager',
        },
      ],
    },
    {
      title: 'Work',
      items: [
        {
          name: t('manager.clients'),
          href: '/manager/clients',
          icon: Icons.building,
          current: isActive('/manager/clients'),
        },
        {
          name: t('manager.projects'),
          href: '/manager/projects',
          icon: Icons.folder,
          current: isActive('/manager/projects'),
        },
        {
          name: t('manager.tasks'),
          href: '/manager/tasks',
          icon: Icons.clipboard,
          current: isActive('/manager/tasks'),
        },
        // {
        //   name: t('nav.clauses'),
        //   href: '/clauses',
        //   icon: Icons.fileText,
        //   current: isActive('/clauses'),
        // },
      ],
    },
    {
      title: 'Team',
      items: [
        {
          name: t('manager.users'),
          href: '/manager/users',
          icon: Icons.user,
          current: isActive('/manager/users'),
        },
        {
          name: t('manager.teams'),
          href: '/manager/teams',
          icon: Icons.users,
          current: isActive('/manager/teams'),
        },
        // skills are managed by admins; shown here rather than in Admin.
        ...(isAdmin
          ? [
              {
                name: t('skills.title', { defaultValue: 'Skills' }),
                href: '/manager/specialties',
                icon: Icons.clipboard,
                current: isActive('/manager/specialties'),
              },
            ]
          : []),
      ],
    },
    {
      title: 'Time',
      items: [
        {
          name: t('manager.timeEntries'),
          href: '/manager/time-entries',
          icon: Icons.clock,
          current: isActive('/manager/time-entries'),
        },
        {
          name: t('manager.timeApproval'),
          href: '/manager/approvals',
          icon: Icons.checkCircle,
          current: isActive('/manager/approvals'),
        },
        {
          name: t('manager.timeOff'),
          href: '/manager/time-off',
          icon: Icons.calendar,
          current: isActive('/manager/time-off'),
        },
      ],
    },
    {
      title: 'Finance',
      items: [
        {
          name: t('manager.pricelists'),
          href: '/manager/pricelists',
          icon: Icons.dollarSign,
          current: isActive('/manager/pricelists'),
        },
        {
          name: t('manager.reports'),
          icon: Icons.fileText,
          children: [
            {
              name: t('manager.reports'),
              href: '/manager/reports',
            },
            {
              name: t('manager.expenseReports'),
              href: '/manager/expenses/reports',
            },
          ],
          current:
            isActive('/manager/reports') ||
            isActive('/manager/expenses/reports'),
        },
        {
          name: t('manager.expenseApprovals'),
          href: '/manager/expense-approvals',
          icon: Icons.checkCircle,
          current: isActive('/manager/expense-approvals'),
        },
      ],
    },
    ...(isAdmin
      ? [
          {
            title: 'Admin',
            items: [
              {
                name: t('manager.organization'),
                href: '/manager/organization',
                icon: Icons.settings,
                current: isActive('/manager/organization'),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Sidebar - hidden on mobile, shown on lg+ */}
      <aside
        className={`hidden lg:flex ${sidebarCollapsed ? 'w-[68px]' : 'w-60'} transition-all duration-300 flex-col ${
          isDark
            ? 'bg-slate-900/80 border-r border-slate-700/50'
            : 'bg-white border-r border-slate-200'
        }`}
      >
        {/* Toggle Button */}
        <div
          className={`p-3 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
        >
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center justify-center p-2 rounded-lg transition-all ${
              isDark
                ? 'text-slate-500 hover:bg-slate-900/50 hover:text-slate-300'
                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
            }`}
          >
            <span
              className={`transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`}
            >
              {Icons.chevronLeft}
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto hide-scrollbar">
          {sections.map((section, sectionIdx) => (
            <div key={section.title} className={sectionIdx > 0 ? 'mt-6' : ''}>
              {!sidebarCollapsed && (
                <h3
                  className={`px-3 mb-2 text-[11px] font-medium uppercase tracking-wide ${
                    isDark ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  {section.title}
                </h3>
              )}
              {sidebarCollapsed && sectionIdx > 0 && (
                <div
                  className={`border-t mb-3 mx-3 ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
                />
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const isExpanded = expandedMenus[item.name];
                  const hasChildren = item.children && item.children.length > 0;

                  return (
                    <li key={item.name} className="relative group">
                      {hasChildren ? (
                        <button
                          onClick={() => {
                            if (!sidebarCollapsed) {
                              setExpandedMenus((prev) => ({
                                ...prev,
                                [item.name]: !isExpanded,
                              }));
                            }
                          }}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                            text-sm font-medium transition-all duration-200
                            ${
                              item.current
                                ? isDark
                                  ? 'bg-indigo-500/10 text-indigo-600'
                                  : 'bg-indigo-50 text-indigo-600'
                                : isDark
                                  ? 'text-slate-400 hover:bg-slate-900/50 hover:text-slate-200'
                                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                            }
                            ${sidebarCollapsed ? 'justify-center' : ''}
                          `}
                        >
                          <div
                            className={`relative flex-shrink-0 ${item.current ? (isDark ? 'text-indigo-600' : 'text-indigo-600') : ''}`}
                          >
                            {item.icon}
                            {item.badge && (
                              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-indigo-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          {!sidebarCollapsed && (
                            <>
                              <span className="flex-1 text-left">
                                {item.name}
                              </span>
                              <span
                                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              >
                                {Icons.chevronDown}
                              </span>
                            </>
                          )}

                          {/* Active indicator */}
                          {item.current && !sidebarCollapsed && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 bg-indigo-500 rounded-r-full" />
                          )}
                        </button>
                      ) : (
                        <Link
                          to={item.href || ''}
                          className={`
                            flex items-center gap-3 px-3 py-2.5 rounded-lg
                            text-sm font-medium transition-all duration-200
                            ${
                              item.current
                                ? isDark
                                  ? 'bg-indigo-500/10 text-indigo-600'
                                  : 'bg-indigo-50 text-indigo-600'
                                : isDark
                                  ? 'text-slate-400 hover:bg-slate-900/50 hover:text-slate-200'
                                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                            }
                            ${sidebarCollapsed ? 'justify-center' : ''}
                          `}
                        >
                          <div
                            className={`relative flex-shrink-0 ${item.current ? (isDark ? 'text-indigo-600' : 'text-indigo-600') : ''}`}
                          >
                            {item.icon}
                            {item.badge && (
                              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-indigo-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          {!sidebarCollapsed && <span>{item.name}</span>}

                          {/* Active indicator */}
                          {item.current && !sidebarCollapsed && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 bg-indigo-500 rounded-r-full" />
                          )}
                        </Link>
                      )}

                      {/* Tooltip for collapsed state */}
                      {sidebarCollapsed && (
                        <div
                          className={`absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 text-xs font-medium rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 ${
                            isDark
                              ? 'bg-slate-900 text-slate-200 border border-slate-700'
                              : 'bg-slate-900 text-white'
                          }`}
                        >
                          {item.name}
                        </div>
                      )}

                      {/* Submenu */}
                      {hasChildren && isExpanded && !sidebarCollapsed && (
                        <ul className="mt-1 ml-4 border-l-2 border-slate-300/30 space-y-1">
                          {item.children &&
                            item.children.map((child) => (
                              <li key={child.href}>
                                <Link
                                  to={child.href}
                                  className={`
                                  flex items-center px-3 py-2 text-sm rounded-lg
                                  transition-all duration-200
                                  ${
                                    isActive(child.href)
                                      ? isDark
                                        ? 'bg-indigo-500/20 text-indigo-400'
                                        : 'bg-indigo-100 text-indigo-700'
                                      : isDark
                                        ? 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                  }
                                `}
                                >
                                  {child.name}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Theme Toggle - TradUp Style */}
        {!sidebarCollapsed && (
          <div
            className={`px-3 py-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
          >
            <ThemeTogglePill />
          </div>
        )}

        {/* Footer - SR&ED/CDAE Badge */}
        <div
          className={`p-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
        >
          <div
            className={`${sidebarCollapsed ? 'p-2 justify-center' : 'px-3 py-2.5'} rounded-lg flex items-center gap-3 ${
              isDark ? 'bg-slate-900/50' : 'bg-slate-50'
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-indigo-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <div
                  className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  SR&ED / CDAE
                </div>
                <div
                  className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                >
                  {t('manager.complianceActive')}
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={`flex-1 overflow-y-auto transition-colors duration-300 ${
          isDark ? 'bg-slate-900/70' : 'bg-slate-50'
        }`}
      >
        <div className="md:p-6">
          <Routes>
            <Route index element={<ManagerOverview />} />
            <Route path="projects/*" element={<ProjectManagement />} />
            <Route path="tasks/*" element={<TaskManagement />} />
            <Route path="users/*" element={<UserManagement />} />
            <Route path="teams/*" element={<TeamManagement />} />
            <Route path="clients/*" element={<ClientManagement />} />
            <Route path="pricelists/*" element={<PricelistManagement />} />
            <Route path="time-entries/*" element={<TimeEntryManagement />} />
            <Route path="approvals/*" element={<TimeApproval />} />
            <Route
              path="expense-approvals/*"
              element={<ExpenseApprovalTable />}
            />
            <Route path="time-off/*" element={<TimeOffManagement />} />
            <Route
              path="time-off-calendar"
              element={<ManagerTimeOffCalendar />}
            />
            <Route path="reports/*" element={<TimeEntryReports />} />
            <Route path="expenses/reports" element={<ExpenseReports />} />
            <Route path="organization/*" element={<OrganizationSettings />} />
            <Route path="activity/*" element={<RecentActivityPage />} />
            <Route
              path="skills"
              element={<Navigate to="/manager/specialties" replace />}
            />
            <Route path="specialties" element={<SpecialtyManagement />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default ManagerDashboard;
